import { useState } from "react"
import { IMask, IMaskInput, IMaskMixin } from "react-imask"
import { P, match } from "ts-pattern"
import { z } from "zod"

import { cn } from "@/lib/utils"
import { IconChevronDown } from "@tabler/icons-react"
import { Column } from "@tanstack/react-table"

import { CommandInput } from "../../Command"
import { Divider } from "../../Divider"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../DropdownMenu"

enum Comparison {
  LessThan = "LessThan",
  GreaterThan = "GreaterThan",
  Between = "Between",
  IsEmpty = "IsEmpty",
  Equal = "Equal",
}

const ComparisonLabel = {
  [Comparison.LessThan]: "less than",
  [Comparison.GreaterThan]: "greater than",
  [Comparison.Between]: "between",
  [Comparison.IsEmpty]: "is empty",
  [Comparison.Equal]: "is equal to",
}

export function NumberRangeFilter<T extends Record<string, any> = object>({
  col,
  filterValue,
}: {
  col: Column<T>
  filterValue?: [number, number, boolean]
}) {
  const maskProps = col.columnDef.meta?.filterNumberMaskOptions ?? {
    thousandsSeparator: ",",
    radix: ".",
    mask: Number,
  }
  const pipeMask = {
    thousandsSeparator: ",",
    radix: ".",
    ...maskProps,
    lazy: false,
  }

  const [minValue, setMinValue] = useState<number | undefined>(() => {
    return filterValue?.[0]
  })
  const [minValueStr, setMinValueStr] = useState<string | undefined>(() => {
    if (filterValue?.[0] !== undefined)
      return IMask.pipe(filterValue[0]?.toString() ?? "", pipeMask)

    return undefined
  })

  const [value, setValue] = useState<number | undefined>(() => {
    return filterValue?.[1]
  })
  const [valueStr, setValueStr] = useState<string | undefined>(() => {
    if (filterValue === undefined) return undefined
    if (filterValue[1] !== undefined)
      return IMask.pipe(filterValue[1]?.toString() ?? "", pipeMask)
    if (filterValue[0] !== undefined)
      return IMask.pipe(filterValue[0]?.toString() ?? "", pipeMask)
    return undefined
  })

  const [comparison, setComparison] = useState<Comparison>(() => {
    if (filterValue === undefined) return Comparison.GreaterThan
    if (filterValue[0] !== undefined && filterValue[1] !== undefined) {
      if (filterValue[0] == filterValue[1]) return Comparison.Equal
      else return Comparison.Between
    }
    if (filterValue[0] !== undefined) return Comparison.GreaterThan
    if (filterValue[1] !== undefined) return Comparison.LessThan
    if (filterValue[2] !== undefined) return Comparison.IsEmpty
    return Comparison.GreaterThan
  })

  const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
  if (typeof label !== "string") {
    throw new Error(
      "column `header` must be a string, or `meta.textLabel` must be set.",
    )
  }

  const showInput = comparison !== Comparison.IsEmpty

  return (
    <>
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <form
        onSubmit={(event) => {
          event.preventDefault()
          switch (comparison) {
            case Comparison.IsEmpty:
              col.setFilterValue([undefined, undefined, true])
              break
            case Comparison.GreaterThan:
              col.setFilterValue([value, undefined, false])
              break
            case Comparison.LessThan:
              col.setFilterValue([undefined, value, false])
              break
            case Comparison.Between:
              col.setFilterValue([minValue, value, false])
              break
            case Comparison.Equal:
              col.setFilterValue([value, value, false])
              break
            default:
              break
          }
        }}
      >
        <div
          className={cn(
            "-mx-1 grid grid-cols-[auto_auto_minmax(0,1fr)] gap-2 rounded px-1",
            {
              "grid-cols-[auto_auto_minmax(0,1fr)_auto_minmax(0,1fr)]":
                comparison === Comparison.Between,
              "grid-cols-[minmax(0,1fr)]": comparison === Comparison.IsEmpty,
            },
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                "relative flex cursor-default items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-body1 text-colors-text-secondary outline-hidden transition-colors select-none hover:bg-colors-bg-hover focus:bg-colors-bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
              )}
            >
              <span>{ComparisonLabel[comparison]}</span>
              <IconChevronDown size={16} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {Object.entries(ComparisonLabel).map(([key, value]) => (
                <DropdownMenuItem
                  key={key}
                  onSelect={() =>
                    setComparison(Comparison[key as keyof typeof Comparison])
                  }
                >
                  {value}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {showInput && <Divider vertical />}

          {comparison === Comparison.Between && (
            <>
              <IMaskInput
                placeholder="Min"
                value={minValueStr !== undefined ? minValueStr : ""}
                onAccept={(v, mask) => {
                  setMinValueStr(v as string)
                  const parsed = z.number().safeParse(Number(mask.unmaskedValue))
                  if (parsed.success) setMinValue(parsed.data)
                }}
                lazy={minValueStr === undefined}
                aria-label="minimum"
                className="w-32 px-2"
                {...maskProps}
              />
              <span className="flex items-center text-body1 text-colors-text-secondary">
                and
              </span>
            </>
          )}
          {showInput && (
            <IMaskInput
              placeholder={match(comparison)
                .with(P.union(Comparison.Between, Comparison.LessThan), () => "Max")
                .with(P.union(Comparison.GreaterThan), () => "Min")
                .with(P.union(Comparison.Equal), () => "Value")
                .exhaustive()}
              value={valueStr !== undefined ? valueStr : ""}
              onAccept={(v, mask) => {
                setValueStr(v as string)
                const parsed = z.number().safeParse(Number(mask.unmaskedValue))
                if (parsed.success) setValue(parsed.data)
              }}
              lazy={valueStr === undefined}
              aria-label={match(comparison)
                .with(P.union(Comparison.Between, Comparison.LessThan), () => "maximum")
                .with(P.union(Comparison.GreaterThan), () => "minimum")
                .with(P.union(Comparison.Equal), () => "Value")
                .exhaustive()}
              className="w-32 px-2"
              {...maskProps}
            />
          )}
        </div>
        <DropdownMenuSeparator />
        <button
          type="submit"
          className="relative flex w-full cursor-default items-center gap-3 rounded-sm px-2 py-1.5 text-body2 outline-hidden transition-colors select-none hover:bg-colors-bg-hover focus:bg-colors-bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
          tabIndex={3}
        >
          Apply
        </button>
      </form>
    </>
  )
}

export const MaskedCommandInput = IMaskMixin(({ inputRef, value, ...props }) => (
  <CommandInput value={value?.toString()} {...props} ref={inputRef} />
)) as typeof IMaskInput
